import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import Header from "../Components/Header";
import Nav from "../Components/Nav";
import { auth, db } from "../config";
import {
  doc,
  getDoc,
  addDoc,
  collection,
  updateDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

function Invest() {
  const [user, setUser] = useState(null);
  const [balance, setBalance] = useState(0);
  const [amounts, setAmounts] = useState({});
  const [messages, setMessages] = useState({});
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        const userDocRef = doc(db, "users", currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setBalance(userDoc.data().balance || 0);
        }
      }
    });

    const fetchPlans = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "investmentPlans"));
        const fetchedPlans = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPlans(fetchedPlans);
      } catch (error) {
        console.error("Error fetching plans:", error);
      }
    };

    fetchPlans();
    return () => unsubscribe();
  }, []);

  const handleInvest = async (e, plan) => {
    e.preventDefault();
    const investmentAmount = parseFloat(amounts[plan.id]);

    if (isNaN(investmentAmount)) {
      setMessages((prev) => ({
        ...prev,
        [plan.id]: "❌ Please enter a valid amount.",
      }));
      return;
    }

    if (investmentAmount < plan.min || investmentAmount > plan.max) {
      setMessages((prev) => ({
        ...prev,
        [plan.id]: `❌ Amount must be between €${plan.min} and €${plan.max}`,
      }));
      return;
    }

    if (investmentAmount > balance) {
      setMessages((prev) => ({
        ...prev,
        [plan.id]: "❌ Insufficient balance.",
      }));
      return;
    }

    try {
      const investmentsRef = collection(db, "investments");
      const activeQuery = query(
        investmentsRef,
        where("userId", "==", user.uid),
        where("plan", "==", plan.name),
        where("status", "==", "active")
      );
      const activeSnapshot = await getDocs(activeQuery);

      if (!activeSnapshot.empty) {
        setMessages((prev) => ({
          ...prev,
          [plan.id]: `❌ You already have an active investment in the "${plan.name}" plan.`,
        }));
        return;
      }

      await addDoc(collection(db, "investments"), {
        userId: user.uid,
        email: user.email,
        plan: plan.name,
        roi: plan.roi,
        duration: plan.duration,
        amount: investmentAmount,
        createdAt: new Date(),
        status: "active",
      });

      const userDocRef = doc(db, "users", user.uid);
      const newBalance = balance - investmentAmount;
      await updateDoc(userDocRef, {
        balance: newBalance,
      });

      setBalance(newBalance);
      setAmounts((prev) => ({ ...prev, [plan.id]: "" }));
      setMessages((prev) => ({
        ...prev,
        [plan.id]: "✅ Investment activated successfully!",
      }));
    } catch (error) {
      setMessages((prev) => ({
        ...prev,
        [plan.id]: "❌ Failed to activate investment: " + error.message,
      }));
    }
  };

  return (
    <div>
      <Helmet>
        <script src="forthfunded/popper.min.js" />
        <script src="forthfunded/bootstrap.bundle.min.js" />
        <script src="forthfunded/defaultmenu.min.js" />
        <script src="forthfunded/waves.min.js" />
        <script src="forthfunded/sticky.js" />
        <script src="forthfunded/simplebar.min.js" />
        <script src="forthfunded/simplebar.js" />
        <script src="forthfunded/pickr.es5.min.js" />
        <script src="forthfunded/apexcharts.min.js" />
        <script src="forthfunded/crypto-dashboard.js" />
        <script src="forthfunded/custom-switcher.min.js" />
        <script src="forthfunded/custom.js" />
      </Helmet>


      <div className="page">
        <Header />
        <Nav />

        <div className="main-content app-content">
          <div className="container-fluid">
            <div className="container-lg">
              <h5 className="fw-semibold text-center">
                Active Investment Packages
              </h5>
              <p className="text-muted text-center">
                Choose from any of the investment plans and start earning
              </p>

              <div className="row mb-5">
                {plans.map((plan) => (
                  <form
                    key={plan.id}
                    onSubmit={(e) => handleInvest(e, plan)}
                    className="col-xxl-4 col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-4"
                  >
                    <div className="card custom-card overflow-hidden border border-primary">
                      <div className="card-body p-0">
                        <div className="px-1 py-2 bg-primary op-5" />
                        <div className="p-4">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <div className="fs-18 fw-semibold">
                              {plan.name}
                            </div>
                            <span className="badge bg-success">
                              {plan.name.toUpperCase()} PLAN
                            </span>
                          </div>

                          <div className="fs-25 fw-bold mb-1">
                            €{plan.min}
                            <sub className="text-muted fw-semibold fs-11 ms-1">
                              / Starting
                            </sub>
                          </div>

                          <div className="text-muted mb-2">
                            Earn {plan.roi}% daily for {plan.duration} days.
                          </div>

                          <ul className="list-unstyled mb-0">
                            <li className="mb-2">Min: €{plan.min}</li>
                            <li className="mb-2">Max: €{plan.max}</li>
                            <li className="mb-2">ROI: {plan.roi}% daily</li>
                            <li className="mb-2">Duration: {plan.duration} days</li>
                            <li className="mb-2">24/7 Support</li>

                            <li className="d-grid mt-2">
                              <input
                                type="number"
                                name="amount"
                                className="form-control"
                                value={amounts[plan.id] || ""}
                                onChange={(e) =>
                                  setAmounts((prev) => ({
                                    ...prev,
                                    [plan.id]: e.target.value,
                                  }))
                                }
                                required
                                placeholder="Enter Investment Amount"
                                style={{ backgroundColor: "#f5f5f5" }}
                              />
                            </li>

                            <li className="d-grid mt-3">
                              <select
                                className="form-control"
                                disabled
                                style={{ backgroundColor: "#f5f5f5" }}
                              >
                                <option>Account Balance - €{balance}</option>
                              </select>
                            </li>

                            <li className="d-grid mt-3">
                              <button
                                type="submit"
                                className="btn btn-primary btn-wave"
                              >
                                Invest in {plan.name}
                              </button>
                            </li>
                          </ul>

                          <div className="mt-3 text-center text-danger fw-bold">
                            {messages[plan.id] && messages[plan.id]}
                          </div>
                        </div>
                      </div>
                    </div>
                  </form>
                ))}
              </div>

              <div className="col-xl-12 mt-5">
                <h5 className="fw-semibold text-center">
                  Try Compounding Package
                </h5>
                <p className="text-muted text-center">
                  Click the button below to continue
                </p>
                <div className="d-flex justify-content-center mb-4">
                  <a
                    href="compoundingagreement"
                    className="nav-link active btn btn-primary"
                  >
                    Compounding Package
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <footer className="footer mt-auto py-3 bg-white text-center">
          <div className="container">
            <span className="text-muted">© 2025 FarYieldFi</span>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default Invest;
